import { useEffect, useMemo, useState } from "react";
import { useClubScope } from "../contexts/ClubScopeContext";
import { fetchStudentSportContext } from "../utils/api/api";
import { resolveClubId } from "../utils/clubScope";

function groupMembershipSignature(user) {
  if (!user) return "";
  const gids = user.groupIDs;
  const gidsStr = Array.isArray(gids) ? gids.join(",") : String(gids ?? "");
  return `${user.groupID ?? ""}|${gidsStr}`;
}

/**
 * Sport context for the active profile (userInfo[0]), including family account switches.
 * Loads from /api/users/:id/sport-context whenever membership groups change (not stale api/me sport).
 */
export function useActiveMemberSport(userInfo) {
  const { apiMe } = useClubScope();
  const activeUser = userInfo?.[0];
  const activeStudentId = activeUser?.studentID;
  const clubID = resolveClubId(userInfo);
  const groupSig = groupMembershipSignature(activeUser);

  const [sport, setSport] = useState(null);
  const [sportStudentId, setSportStudentId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (activeStudentId == null || activeStudentId === "") {
      setSport(null);
      setSportStudentId(null);
      setLoaded(false);
      return;
    }

    setLoaded(false);
    let cancelled = false;

    fetchStudentSportContext(activeStudentId, clubID)
      .then((data) => {
        if (!cancelled) {
          setSport(data?.sport ?? null);
          setSportStudentId(activeStudentId);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          const sessionId = apiMe?.studentID != null ? Number(apiMe.studentID) : null;
          const sessionMatches =
            sessionId != null && Number(activeStudentId) === sessionId;
          setSport(sessionMatches ? apiMe?.sport ?? null : null);
          setSportStudentId(activeStudentId);
          setLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeStudentId, clubID, groupSig, apiMe?.studentID, apiMe?.sport]);

  return useMemo(() => {
    if (activeStudentId == null || activeStudentId === "") {
      return { sport: null, loaded: false };
    }

    const cacheMatches =
      sportStudentId != null && String(sportStudentId) === String(activeStudentId);

    return {
      sport: cacheMatches ? sport : null,
      loaded: cacheMatches && loaded,
    };
  }, [activeStudentId, sport, sportStudentId, loaded]);
}
